import React from 'react';
import { PartnerIcon, CommunityIcon, ThinkIcon } from '../../icons';
import { Lightbulb } from 'lucide-react';

export type EntityType = 'networkclub' | 'community' | 'mastermind' | null;

interface NetworkclubStepProps {
  data: {
    representsNetworkclub: boolean | null; // Legacy field for backwards compatibility
    entityType: EntityType;
    networkclubName: string;
    networkclubKvk: string;
  };
  setData: (data: any) => void;
  invalidFields: Set<string>;
  setInvalidFields: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const NetworkclubStep: React.FC<NetworkclubStepProps> = ({
  data,
  setData,
  invalidFields,
  setInvalidFields
}) => {
  const setChoice = (value: EntityType) => {
    setData({
      ...data,
      entityType: value,
      representsNetworkclub: value !== null, // Update legacy field
      networkclubName: value ? data.networkclubName : '',
      networkclubKvk: value ? data.networkclubKvk : ''
    });
    if (invalidFields.has('entityType')) {
      const next = new Set(invalidFields);
      next.delete('entityType');
      next.delete('representsNetworkclub');
      setInvalidFields(next);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Jouw Organisatie</h2>
        <p className="text-gray-600">Vertegenwoordig of organiseer je een netwerkclub, community of mastermind?</p>
      </div>

      <div className="space-y-3">
        {/* Nee - geen organisatie */}
        <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:border-gray-300 transition-colors">
          <input
            type="radio"
            name="entityType"
            checked={data.entityType === null}
            onChange={() => setChoice(null)}
            className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <span className="font-medium text-gray-900">Nee</span>
            <p className="text-xs text-gray-500 mt-0.5">Ik vertegenwoordig geen organisatie</p>
          </div>
        </label>

        {/* Netwerkclub */}
        <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
          data.entityType === 'networkclub' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
        }`}>
          <input
            type="radio"
            name="entityType"
            checked={data.entityType === 'networkclub'}
            onChange={() => setChoice('networkclub')}
            className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <span className="font-medium text-gray-900 inline-flex items-center gap-1.5"><PartnerIcon size={18} weight="regular" className="text-gray-600" /> Netwerkclub</span>
            <p className="text-xs text-gray-500 mt-0.5">Voor formele netwerkorganisaties met regelmatige bijeenkomsten</p>
          </div>
        </label>

        {/* Community */}
        <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
          data.entityType === 'community' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
        }`}>
          <input
            type="radio"
            name="entityType"
            checked={data.entityType === 'community'}
            onChange={() => setChoice('community')}
            className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <span className="font-medium text-gray-900 inline-flex items-center gap-1.5"><CommunityIcon size={18} weight="regular" className="text-gray-600" /> Community</span>
            <p className="text-xs text-gray-500 mt-0.5">Voor open communities rondom een thema of interesse</p>
          </div>
        </label>

        {/* Mastermind */}
        <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
          data.entityType === 'mastermind' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
        }`}>
          <input
            type="radio"
            name="entityType"
            checked={data.entityType === 'mastermind'}
            onChange={() => setChoice('mastermind')}
            className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <span className="font-medium text-gray-900 inline-flex items-center gap-1.5"><ThinkIcon size={18} weight="regular" className="text-gray-600" /> Mastermind</span>
            <p className="text-xs text-gray-500 mt-0.5">Voor besloten groepen met intensieve samenwerking en groei</p>
          </div>
        </label>

        {invalidFields.has('entityType') && (
          <p className="text-sm text-[#B5724C]">Maak een keuze om verder te gaan.</p>
        )}
      </div>

      {/* Info message when entity is selected */}
      {data.entityType && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong className="inline-flex items-center gap-1"><Lightbulb size={14} className="text-blue-700" /> Let op:</strong> Om leden uit te nodigen en je {
              data.entityType === 'networkclub' ? 'netwerkclub' :
              data.entityType === 'community' ? 'community' : 'mastermind'
            } publiekelijk zichtbaar te maken, heb je een abonnement nodig. 
            Zonder abonnement blijft deze alleen voor jou zichtbaar.
          </p>
        </div>
      )}
    </div>
  );
};

export default NetworkclubStep;
